'use client';

import React, { useState } from 'react';

// ─── SECTION MANAGEMENT PANEL ─────────────────────────────────────────────────
// Shows every letter section with its PDF page number and current render position.
// Allows the user to see at a glance if any section is out of PDF order.
// Each section can be individually noted for repositioning.

interface SectionEntry {
  id: string;
  title: string;
  pdfPages: string;
  currentPosition: number;
  pdfPosition: number;
  component: string;
  anchor: string;
  status: 'correct' | 'review' | 'note';
  note?: string;
}

const SECTIONS: SectionEntry[] = [
  // ── LetterBody (LetterComponents) ──────────────────────────────────────────
  { id: 'opening', title: 'Opening / Recognition', pdfPages: '1–2', currentPosition: 1, pdfPosition: 1, component: 'LetterBody → section-opening', anchor: '#section-opening', status: 'correct' },
  { id: 'caretaker', title: 'Caretaker', pdfPages: '2–3', currentPosition: 2, pdfPosition: 2, component: 'LetterBody → section-caretaker', anchor: '#section-caretaker', status: 'correct' },
  { id: 'the-mural', title: 'The Mural', pdfPages: '3–4', currentPosition: 3, pdfPosition: 3, component: 'LetterBody → section-the-mural', anchor: '#section-the-mural', status: 'correct' },
  { id: 'roots-marifah', title: "ROOTS Ma'rifah", pdfPages: '5–9', currentPosition: 4, pdfPosition: 4, component: 'LetterBody → section-roots-marifah', anchor: '#section-roots-marifah', status: 'correct' },
  // ── Plot4Content ───────────────────────────────────────────────────────────
  { id: 'al-qadir', title: 'Al-Qadir — The All-Powerful', pdfPages: '9–12', currentPosition: 5, pdfPosition: 5, component: 'Plot4Content → AlQadirSection', anchor: '#section-al-qadir', status: 'correct' },
  { id: 'aql', title: 'Aql — Intellect', pdfPages: '13–14', currentPosition: 6, pdfPosition: 6, component: 'Plot4Content → AqlSection', anchor: '#section-aql', status: 'correct' },
  { id: 'ilm', title: 'Ilm — Knowledge', pdfPages: '14–15', currentPosition: 7, pdfPosition: 7, component: 'Plot4Content → IlmSection', anchor: '#section-ilm', status: 'correct' },
  { id: 'hawas', title: 'Hawas — Senses', pdfPages: '15–17', currentPosition: 8, pdfPosition: 8, component: 'Plot4Content → HawasSection', anchor: '#section-hawas', status: 'correct' },
  { id: 'al-alim-al-hakim', title: 'Al-Alim · Al-Hakim', pdfPages: '17–18', currentPosition: 9, pdfPosition: 9, component: 'Plot4Content → AlAlimAlHakimSection', anchor: '#section-al-alim-al-hakim', status: 'correct' },
  { id: 'entering-haal', title: '[Entering the Haal Platform]', pdfPages: '18–21', currentPosition: 10, pdfPosition: 10, component: 'Plot4Content → EnteringHaalPlatformSection', anchor: '#section-entering-haal', status: 'correct' },
  { id: 'islah', title: 'Islah — The Complete Fix', pdfPages: '21–22', currentPosition: 11, pdfPosition: 11, component: 'Plot4Content → IslahSection', anchor: '#section-islah', status: 'correct' },
  // ── Plot4ArRahmanJibril ────────────────────────────────────────────────────
  { id: 'ar-rahman-compassion', title: 'Ar-Rahman — Compassion', pdfPages: '22–23', currentPosition: 12, pdfPosition: 12, component: 'Plot4ArRahmanJibril → ArRahmanCompassionSection', anchor: '#section-ar-rahman-compassion', status: 'correct' },
  { id: 'hadith-jibril', title: 'Hadith of Jibril — Islam · Iman · Ihsan', pdfPages: '24–25', currentPosition: 13, pdfPosition: 13, component: 'Plot4ArRahmanJibril → HadithJibrilSection', anchor: '#section-hadith-jibril', status: 'correct' },
  // ── Plot4MissingContent ────────────────────────────────────────────────────
  { id: 'al-haseeb-ar-raqib', title: 'Al-Haseeb · Ar-Raqib — Poem', pdfPages: '26–27', currentPosition: 14, pdfPosition: 14, component: 'Plot4MissingContent → AlHaseebArRaqibSection', anchor: '#section-al-haseeb-ar-raqib', status: 'correct' },
  { id: 'till-rows', title: 'Till Rows', pdfPages: '26–27', currentPosition: 15, pdfPosition: 15, component: 'Plot4MissingContent → TillRowsSection', anchor: '#section-till-rows', status: 'correct' },
  { id: 'rope-of-language', title: 'Rope of Language', pdfPages: '28–30', currentPosition: 16, pdfPosition: 16, component: 'Plot4MissingContent → RopeOfLanguageSection', anchor: '#section-rope-of-language', status: 'correct' },
  { id: 'amanah-trust', title: 'Amanah — Trust', pdfPages: '31–32', currentPosition: 17, pdfPosition: 17, component: 'Plot4MissingContent → AmanahTrustSection', anchor: '#section-amanah-trust', status: 'correct' },
  { id: 'haqq', title: 'Haqq — Truth · Right · Reality', pdfPages: '32–34', currentPosition: 18, pdfPosition: 18, component: 'Plot4MissingContent → HaqqSection', anchor: '#section-haqq', status: 'correct' },
  { id: 'al-khaliq', title: 'Al-Khaliq — The Creator', pdfPages: '39–41', currentPosition: 19, pdfPosition: 19, component: 'Plot4MissingContent → AlKhaliqSection', anchor: '#section-al-khaliq', status: 'correct' },
  // ── Plot4AlKhabirZahir ─────────────────────────────────────────────────────
  { id: 'al-khabir-al-zahir', title: 'Al-Khabir · Al-Zahir', pdfPages: '41–43', currentPosition: 20, pdfPosition: 20, component: 'Plot4AlKhabirZahir → AlKhabirSection + AlZahirSection', anchor: '#section-al-khabir-al-zahir', status: 'correct' },
  // ── Plot4YaqeenContent ─────────────────────────────────────────────────────
  { id: 'yaqeen-framework', title: 'Yaqeen Framework — Cherry Blossom', pdfPages: '3–4', currentPosition: 21, pdfPosition: 4, component: 'Plot4YaqeenContent → YaqeenFrameworkSection', anchor: '#section-yaqeen-framework', status: 'review', note: 'Yaqeen levels (Ilm/Ayn/Haqq) appear in the Mural section of the PDF (pages 3–4). Currently rendered after Al-Zahir (position 21). Consider moving to after The Mural section.' },
  // ── Plot4Enhancements ──────────────────────────────────────────────────────
  { id: 'plot4-enhancements', title: 'Two-Eye · Three-Mode · Fitra Diagnostic · 41:53', pdfPages: 'Platform additions', currentPosition: 22, pdfPosition: 22, component: 'Plot4Enhancements', anchor: '#section-plot4-enhancements', status: 'note', note: 'Platform-generated enhancements — no direct PDF equivalent. Positioned after core letter content.' },
  // ── VocationalNetwork / SynthesisPanel / DailyQuestGenerator ──────────────
  { id: 'vocational-network', title: 'Vocational Network', pdfPages: 'Platform addition', currentPosition: 23, pdfPosition: 23, component: 'VocationalNetwork', anchor: '#section-vocational-network', status: 'note', note: 'Platform feature derived from role-questioning cascade (PDF pages 12–13). No direct PDF section.' },
  { id: 'synthesis-panel', title: 'Synthesis Panel + Isnad', pdfPages: 'Platform addition', currentPosition: 24, pdfPosition: 24, component: 'SynthesisPanelIsnad', anchor: '#section-synthesis-panel', status: 'note', note: 'Platform feature. No direct PDF equivalent.' },
  { id: 'daily-quest', title: 'Daily Quest Generator', pdfPages: 'Platform addition', currentPosition: 25, pdfPosition: 25, component: 'DailyQuestGenerator', anchor: '#section-daily-quest', status: 'note', note: 'Platform feature. No direct PDF equivalent.' },
  // ── Plot5 ──────────────────────────────────────────────────────────────────
  { id: 'plot5', title: 'Root Modules · Vocation Lens · Revelation Language', pdfPages: 'Platform additions', currentPosition: 26, pdfPosition: 26, component: 'Plot5Enhancements', anchor: '#section-plot5', status: 'note', note: 'Platform-generated enhancements.' },
];

const STATUS_COLORS: Record<string, string> = {
  correct: 'rgba(45,122,71,0.7)',
  review: 'rgba(201,168,76,0.8)',
  note: 'rgba(138,138,184,0.7)',
};

const STATUS_LABELS: Record<string, string> = {
  correct: '✓ In PDF order',
  review: '⚠ Review position',
  note: '◈ Platform addition',
};

export default function SectionManagementPanel() {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'correct' | 'review' | 'note'>('all');
  const [expandedNote, setExpandedNote] = useState<string | null>(null);

  const filtered = SECTIONS.filter(s => filter === 'all' || s.status === filter);
  const reviewCount = SECTIONS.filter(s => s.status === 'review').length;

  return (
    <div style={{ background: 'rgba(6,13,18,0.97)', borderTop: '1px solid rgba(201,168,76,0.08)', padding: '40px 0' }}>
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        {/* Header toggle */}
        <button
          onClick={() => setOpen(!open)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            background: 'rgba(201,168,76,0.04)',
            border: '1px solid rgba(201,168,76,0.15)',
            borderRadius: 8,
            padding: '14px 20px',
            cursor: 'pointer',
            color: 'rgba(212,197,169,0.7)',
          }}
          aria-expanded={open}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.6)' }}>
              ◈ Section Management
            </span>
            <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)' }}>
              {SECTIONS.length} sections · PDF cross-reference
            </span>
            {reviewCount > 0 && (
              <span style={{ fontFamily: 'monospace', fontSize: 9, background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 4, padding: '2px 8px', color: '#c9a84c' }}>
                {reviewCount} to review
              </span>
            )}
          </div>
          <span style={{ color: 'rgba(201,168,76,0.5)', fontSize: 12, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
        </button>

        {open && (
          <div style={{ marginTop: 16, animation: 'efFadeIn 0.22s ease' }}>
            <style>{`@keyframes efFadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>

            {/* Description */}
            <p style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(212,197,169,0.4)', lineHeight: 1.7, marginBottom: 16 }}>
              Every section of the open letter mapped to its PDF page number and current render position. Use this to identify any ordering discrepancies and navigate directly to any section.
            </p>

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {(['all', 'correct', 'review', 'note'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  style={{
                    fontFamily: 'monospace',
                    fontSize: 9,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    padding: '5px 12px',
                    border: `1px solid ${filter === f ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.08)'}`,
                    background: filter === f ? 'rgba(201,168,76,0.08)' : 'transparent',
                    color: filter === f ? '#c9a84c' : 'rgba(212,197,169,0.4)',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                >
                  {f === 'all' ? `All (${SECTIONS.length})` : f === 'correct' ? `✓ In order (${SECTIONS.filter(s => s.status === 'correct').length})` : f === 'review' ? `⚠ Review (${SECTIONS.filter(s => s.status === 'review').length})` : `◈ Platform (${SECTIONS.filter(s => s.status === 'note').length})`}
                </button>
              ))}
            </div>

            {/* Section list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {filtered.map((section, idx) => (
                <div
                  key={section.id}
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: `1px solid ${section.status === 'review' ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.05)'}`,
                    borderRadius: 6,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 14px',
                      cursor: section.note ? 'pointer' : 'default',
                    }}
                    onClick={() => section.note ? setExpandedNote(expandedNote === section.id ? null : section.id) : undefined}
                  >
                    {/* Position number */}
                    <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.25)', width: 20, flexShrink: 0, textAlign: 'right' }}>
                      {section.currentPosition}
                    </span>

                    {/* Status dot */}
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_COLORS[section.status], flexShrink: 0 }} />

                    {/* Title */}
                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(212,197,169,0.75)', flex: 1, minWidth: 0 }}>
                      {section.title}
                    </span>

                    {/* PDF pages */}
                    <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.5)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                      PDF p.{section.pdfPages}
                    </span>

                    {/* Navigate link */}
                    <a
                      href={section.anchor}
                      onClick={e => e.stopPropagation()}
                      style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(45,138,135,0.6)', textDecoration: 'none', flexShrink: 0, padding: '2px 8px', border: '1px solid rgba(45,138,135,0.2)', borderRadius: 3 }}
                    >
                      ↓ Go
                    </a>

                    {/* Expand indicator for notes */}
                    {section.note && (
                      <span style={{ color: 'rgba(201,168,76,0.4)', fontSize: 10, flexShrink: 0, transform: expandedNote === section.id ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>▼</span>
                    )}
                  </div>

                  {/* Expanded note */}
                  {expandedNote === section.id && section.note && (
                    <div style={{ padding: '8px 14px 12px 50px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                      <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.45)', lineHeight: 1.7 }}>
                        <span style={{ color: STATUS_COLORS[section.status] }}>{STATUS_LABELS[section.status]}</span>
                        {' — '}{section.note}
                      </p>
                      <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.25)', marginTop: 4 }}>
                        Component: {section.component}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
              {Object.entries(STATUS_LABELS).map(([key, label]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_COLORS[key], flexShrink: 0 }} />
                  <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.35)' }}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
